import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/ui/utils/cx';

export type AppDropdownOption = {
  value: string;
  label: string;
  description?: string;
  /** Shown on the closed trigger; defaults to label or label — description */
  triggerLabel?: string;
  disabled?: boolean;
};

export type AppDropdownSelectProps = {
  id?: string;
  value: string;
  options: AppDropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  inputSize?: 'sm' | 'md' | 'lg';
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

function optionTriggerLabel(option: AppDropdownOption): string {
  if (option.triggerLabel) return option.triggerLabel;
  if (option.description) return `${option.label} — ${option.description}`;
  return option.label;
}

const AppDropdownSelect = React.forwardRef<HTMLButtonElement, AppDropdownSelectProps>(
  function AppDropdownSelect(
    {
      id: idProp,
      value,
      options,
      onChange,
      disabled = false,
      hasError = false,
      placeholder = 'Select…',
      inputSize = 'md',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
    },
    ref,
  ) {
    const autoId = useId();
    const listboxId = `${autoId.replace(/:/g, '')}-listbox`;
    const triggerId = idProp ?? `${autoId.replace(/:/g, '')}-trigger`;
    const [open, setOpen] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

    const selectableOptions = useMemo(
      () => options.filter((option) => !option.disabled),
      [options],
    );

    const selectedOption = useMemo(
      () => options.find((option) => option.value === value) ?? null,
      [options, value],
    );

    const selectedLabel = selectedOption
      ? optionTriggerLabel(selectedOption)
      : placeholder;

    const updatePosition = useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    }, []);

    useEffect(() => {
      if (!open) return;
      updatePosition();
      function onScroll() {
        updatePosition();
      }
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', onScroll, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', onScroll, true);
      };
    }, [open, updatePosition]);

    useEffect(() => {
      if (!open) return;
      function onPointerDown(event: MouseEvent) {
        const target = event.target as Node;
        if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
        setOpen(false);
      }
      function onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false);
      }
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('mousedown', onPointerDown);
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [open]);

    useEffect(() => {
      if (!open) return;
      const index = Math.max(
        0,
        selectableOptions.findIndex((option) => option.value === value),
      );
      setHighlightIndex(index >= 0 ? index : 0);
    }, [open, selectableOptions, value]);

    const close = () => setOpen(false);

    const selectOption = (option: AppDropdownOption) => {
      if (option.disabled) return;
      onChange(option.value);
      close();
      triggerRef.current?.focus();
    };

    const onPanelKeyDown = (event: React.KeyboardEvent) => {
      if (!selectableOptions.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightIndex((index) => (index + 1) % selectableOptions.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightIndex(
          (index) => (index - 1 + selectableOptions.length) % selectableOptions.length,
        );
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const option = selectableOptions[highlightIndex];
        if (option) selectOption(option);
      } else if (event.key === 'Home') {
        event.preventDefault();
        setHighlightIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setHighlightIndex(selectableOptions.length - 1);
      }
    };

    const setTriggerRef = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    return (
      <div ref={rootRef} className="app-dropdown-select">
        <button
          ref={setTriggerRef}
          id={triggerId}
          type="button"
          className={cx(
            'app-dropdown-select__trigger app-control',
            inputSize !== 'md' && `app-control--${inputSize}`,
            hasError && 'app-control--error',
            open && 'is-open',
          )}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          title={selectedLabel}
          onClick={() => {
            if (disabled) return;
            setOpen((current) => !current);
          }}
        >
          <span className="app-dropdown-select__value">{selectedLabel}</span>
          <span className="app-dropdown-select__chevron" aria-hidden>
            ▾
          </span>
        </button>
        {open &&
          createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              className="app-dropdown-select__panel"
              role="listbox"
              aria-labelledby={ariaLabelledBy ?? triggerId}
              style={{
                top: position.top,
                left: position.left,
                minWidth: Math.max(position.width, 240),
              }}
              onKeyDown={onPanelKeyDown}
            >
              {options.map((option) => {
                const selected = option.value === value;
                const highlighted =
                  selectableOptions[highlightIndex]?.value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={option.disabled}
                    className={cx(
                      'app-dropdown-select__option',
                      selected && 'is-selected',
                      highlighted && 'is-highlighted',
                      option.disabled && 'is-disabled',
                    )}
                    onMouseEnter={() => {
                      const index = selectableOptions.findIndex(
                        (item) => item.value === option.value,
                      );
                      if (index >= 0) setHighlightIndex(index);
                    }}
                    onClick={() => selectOption(option)}
                  >
                    <span className="app-dropdown-select__option-text">
                      <span className="app-dropdown-select__option-label">{option.label}</span>
                      {option.description ? (
                        <span className="app-dropdown-select__option-description">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                    {selected ? (
                      <span className="app-dropdown-select__check" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )}
      </div>
    );
  },
);

export default AppDropdownSelect;
